import experiment from 'experiment';
import * as stateManagement from '#packages/stateManagement';
import { SiteGlobalDataApiKey } from '#packages/apis';
import { fedopsLogger } from '#packages/util';
import constants from '#packages/constants';
import { utils as coreUtils } from '#packages/core';
import { IGNORE_INJECTION_PAGES_TITLES, PANEL_NAME } from './constants';
import { INJECTION_MASTER_PAGE_WHITE_LIST } from '../../injectionMasterPageWhiteList';
import type {
  Dispatch,
  MapDispatchToProps,
  MapStateToProps,
  ThunkAction,
} from 'types/redux';
import type {
  DispatchProps,
  ErrorData,
  OnInjectionSuccessFnParams,
  QuestionnaireParams,
  StateProps,
  SuccessPageInjectionResponse,
  SuccessSiteInjectionResponse,
} from './types';
import type { EditorState } from '#packages/stateManagement';
import type { EditorAPI } from '#packages/editorAPI';
import { HttpClient, type HttpResponse } from '@wix/http-client';
import {
  isApplicable,
  getSiteDescription,
  getContext,
} from '@wix/ambassador-genie-v1-compose/http';
import type {
  IsApplicableResponse,
  GetSiteDescriptionResponse,
  GetContextResponse,
} from '@wix/ambassador-genie-v1-compose/types';
import { ErrorReporter } from '@wix/editor-error-reporter';
import {
  getMetaSiteInstance,
  getAiUploadedImages,
  injectAiImages,
  isAiImageInjectionToTemplateEnabled,
} from './utils';
import * as util from '#packages/util';
import { getSuccessNotificationMessage } from './welcomePanelHelpers';

interface AsyncUpdatePageResponse {
  pageId: string;
  response: SuccessPageInjectionResponse;
  requestStartTime: number;
  requestEndTime: number;
}

const setSourceData = (editorAPI: EditorAPI, ids: string[]) => {
  ids.forEach((id: string) => {
    coreUtils.componentSourceFeatureUtils.updateComponentSource(editorAPI, {
      compRef: editorAPI.components.get.byId(id),
      source: 'ai',
    });
  });
};

const updatePagesInParallel = async (
  editorAPI: EditorAPI,
  params: QuestionnaireParams,
  onSuccess: (params: OnInjectionSuccessFnParams) => void,
  onFailure: (response: ErrorData) => void,
  isAutosubmitFlow: boolean,
  actionOrigin: string,
  excludeMasterPage: boolean,
) => {
  const allPages = editorAPI.pages.getPagesList();

  const updatePageContentAsync = async (
    pageId: string,
    requestStartTime: number,
  ): Promise<AsyncUpdatePageResponse> => {
    return new Promise((resolve, reject) => {
      editorAPI.ai.content.updatePageContent(
        pageId,
        params.businessType?.displayName,
        params.businessName,
        params.businessDescription,
        (response: SuccessPageInjectionResponse) => {
          resolve({
            pageId,
            response,
            requestStartTime,
            requestEndTime: Date.now(),
          });
        },
        reject,
        { excludeMasterPage },
      );
    });
  };

  const updatePromises = await editorAPI.documentServices.transactions.run(
    async () => {
      return allPages
        .filter((pageRef) => {
          const pageTitle = editorAPI.pages.getPageTitle(pageRef.id);
          return !IGNORE_INJECTION_PAGES_TITLES.has(pageTitle);
        })
        .map((pageRef) => {
          const requestStartTime = Date.now();

          return updatePageContentAsync(pageRef.id, requestStartTime);
        });
    },
  );

  try {
    const updatedPagesResponses = await Promise.all(updatePromises);

    await editorAPI.documentServices.waitForChangesAppliedAsync();

    const changedComponentsIds: string[] = [];

    updatedPagesResponses.forEach(
      ({ pageId, response, requestStartTime, requestEndTime }) => {
        onSuccess({
          requestParams: params,
          responseParams: [
            {
              pageId,
              pageSuggestion: response.suggestionResults,
            },
          ],
          requestStartTime,
          requestEndTime,
          isAutosubmitFlow,
          actionOrigin,
          isPageByPageFlow: true,
        });

        changedComponentsIds.push(
          ...Object.values(response.suggestionResults.idMap),
        );
      },
    );

    setSourceData(editorAPI, changedComponentsIds);

    fedopsLogger.interactionEnded(
      fedopsLogger.INTERACTIONS.AI_TEXT_CREATOR.SITE_INJECTION,
    );

    editorAPI.history.add('AI Update Page Content');

    const notificationMessage = getSuccessNotificationMessage(actionOrigin);
    if (notificationMessage) {
      showNotification(notificationMessage);
    }
  } catch (error: any) {
    onFailure(error);
  }
};

const mapStateToProps: MapStateToProps<StateProps> = ({ editorAPI }) => {
  const siteGlobalDataApi = editorAPI.host.getAPI(SiteGlobalDataApiKey);
  const businessType = siteGlobalDataApi.getBusinessType();
  const businessName = siteGlobalDataApi.getBusinessName() ?? '';

  return {
    businessType,
    businessName,
  };
};

const onClose = (origin?: string) => (dispatch: Dispatch) => {
  dispatch(stateManagement.panels.actions.closePanelByName(PANEL_NAME, origin));
};

const showNotification =
  (message: string): ThunkAction =>
  (dispatch) => {
    dispatch(
      stateManagement.notifications.actions.showUserActionNotification({
        type: constants.NOTIFICATIONS.TYPES.INFO,
        message,
      }),
    );
  };

const mapDispatchToProps: MapDispatchToProps<DispatchProps, {}> = {
  onClose,
  onSubmit:
    (
      params: QuestionnaireParams,
      isAutosubmitFlow: boolean,
      actionOrigin: string,
      onSuccess: (params: OnInjectionSuccessFnParams) => void,
      onFailure: (response: ErrorData) => void,
    ): ThunkAction =>
    async (dispatch, _, { editorAPI, host }) => {
      const requestStartTime: number = Date.now();
      const pageId = editorAPI.pages.getCurrentPageId();
      const templateId = editorAPI.generalInfo.getSiteOriginalTemplateId();

      dispatch(stateManagement.ai.actions.setIsAiTermsAndConditionsShown(true));

      fedopsLogger.interactionStarted(
        fedopsLogger.INTERACTIONS.AI_TEXT_CREATOR.SITE_INJECTION,
      );

      const excludeMasterPage =
        !experiment.isOpen('se_ignoreInjectionMasterPageWhiteList') &&
        !INJECTION_MASTER_PAGE_WHITE_LIST.has(templateId);

      if (experiment.isOpen('se_aiInjectionIntoTemplateAllPages')) {
        let aiImages;
        if (isAiImageInjectionToTemplateEnabled()) {
          const msInstance = getMetaSiteInstance(editorAPI);
          const httpClient = new HttpClient({
            headers: { Authorization: msInstance },
          });

          await util.createOrUpdateSiteInsight(
            httpClient,
            params.businessDescription,
          );

          const pagesList = editorAPI.pages.getPagesList();
          const allSections = pagesList.flatMap((page) =>
            editorAPI.sections.getPageSections(page),
          );

          aiImages = await getAiUploadedImages({
            editorAPI,
            allSections,
            msInstance,
            businessTerm: params.businessType?.displayName,
            siteDescription: params.businessDescription,
          });
        }

        if (experiment.isOpen('se_excludeLegalPagesFromAiTemplateInjection')) {
          await updatePagesInParallel(
            editorAPI,
            params,
            onSuccess,
            onFailure,
            isAutosubmitFlow,
            actionOrigin,
            excludeMasterPage,
          );

          dispatch(onClose('closed after success result'));
        } else {
          editorAPI.ai.content.updateSiteContent(
            params.businessType?.displayName,
            params.businessName,
            params.businessDescription,
            (response: SuccessSiteInjectionResponse) => {
              onSuccess({
                requestParams: params,
                responseParams: response,
                requestStartTime,
                isAutosubmitFlow,
                actionOrigin,
              });

              const changedComponentsIds = response.reduce(
                (accumulator, currentValue) => {
                  accumulator.push(
                    ...Object.values(currentValue?.pageSuggestion?.idMap ?? {}),
                  );

                  return accumulator;
                },
                [],
              );

              setSourceData(editorAPI, changedComponentsIds);

              fedopsLogger.interactionEnded(
                fedopsLogger.INTERACTIONS.AI_TEXT_CREATOR.SITE_INJECTION,
              );

              editorAPI.history.add('AI Update Site Content');
            },
            onFailure,
            { excludeMasterPage },
          );
        }
        if (isAiImageInjectionToTemplateEnabled()) {
          injectAiImages(editorAPI, aiImages);
        }
      } else {
        editorAPI.ai.content.updatePageContent(
          pageId,
          params.businessType?.displayName,
          params.businessName,
          params.businessDescription,
          (response: SuccessPageInjectionResponse) => {
            onSuccess({
              requestParams: params,
              responseParams: [
                {
                  pageId,
                  pageSuggestion: response.suggestionResults,
                },
              ],
              requestStartTime,
              isAutosubmitFlow,
              actionOrigin,
            });

            const changedComponentsIds: string[] = Object.values(
              response.suggestionResults.idMap,
            );
            setSourceData(editorAPI, changedComponentsIds);

            fedopsLogger.interactionEnded(
              fedopsLogger.INTERACTIONS.AI_TEXT_CREATOR.SITE_INJECTION,
            );

            editorAPI.history.add('AI Update Page Content');
          },
          onFailure,
          { excludeMasterPage },
        );
      }

      const siteGlobalDataAPI = host.getAPI(SiteGlobalDataApiKey);
      siteGlobalDataAPI.setBusinessType(params.businessType);
    },
  highlightLeftBarMenu: () => (dispatch: Dispatch) => {
    dispatch(stateManagement.leftBar.actions.highlightMenu());
  },
  unHighlightLeftBarMenu: () => (dispatch: Dispatch) => {
    dispatch(stateManagement.leftBar.actions.unhighlightMenu());
  },
  getParentId:
    (id: string) =>
    (
      dispatch: Dispatch,
      getState: () => EditorState,
      { editorAPI }: { editorAPI: EditorAPI },
    ) =>
      editorAPI.components.getContainer(editorAPI.components.get.byId(id))?.id,
  getAdditionalBIData:
    (componentId: string) =>
    (
      dispatch: Dispatch,
      getState: () => EditorState,
      { editorAPI }: { editorAPI: EditorAPI },
    ) => {
      //TODO: investigate sentry reports, why componentRef is null
      try {
        const componentRef = editorAPI.components.get.byId(componentId);
        const pageId = editorAPI.components.getPage(componentRef).id;
        const sectionType =
          editorAPI.sections.getClosestSection(componentRef)?.type || 'null';

        return {
          componentType: componentRef.type,
          pageId,
          parentComponentType:
            editorAPI.components.getContainer(componentRef)?.type,
          sectionType,
        };
      } catch (e) {
        ErrorReporter.captureException(e, {
          tags: { aiWelcomePanelMapperBiAdditionalData: true },
          extra: {
            componentId,
          },
        });
      }
    },
  isFunnelDataApplicable:
    (onSuccess: (result: boolean) => void): ThunkAction =>
    async (_dispatch, _getState, { editorAPI }) => {
      const httpClient = new HttpClient({
        getAppToken: () =>
          editorAPI.documentServices.platform.getAppDataByApplicationId('-666')
            ?.instance,
      });

      let result: boolean;
      try {
        const response: HttpResponse<IsApplicableResponse> =
          await httpClient.request(isApplicable({}));
        result = response?.data?.applicable ?? false;
      } catch (err) {
        result = false;
      }

      onSuccess(result);
    },
  getDescriptionFromFunnel:
    (onSuccess: (result: string) => void): ThunkAction =>
    async (_dispatch, _getState, { editorAPI }) => {
      const httpClient = new HttpClient({
        getAppToken: () =>
          editorAPI.documentServices.platform.getAppDataByApplicationId(
            constants.APPLICATIONS.META_SITE_APPLICATION_ID,
          )?.instance,
      });

      if (experiment.isOpen('se_useFunnelSiteDescription')) {
        const responce: HttpResponse<GetSiteDescriptionResponse> =
          await httpClient.request(getSiteDescription({}));

        onSuccess(responce.data.siteDescription.value);
        return;
      }

      const responce: HttpResponse<GetContextResponse> =
        await httpClient.request(getContext({}));

      onSuccess(responce.data.context.value);
    },
  showNotification,
};

const mergeProps = (
  { businessType, businessName }: StateProps,
  dispatchProps: DispatchProps,
  ownProps: {},
) => {
  const { onSubmit, getDescriptionFromFunnel, ...restDispatchProps } =
    dispatchProps;

  const autoSubmitForm = async (
    isFunnelDataApplicable: boolean,
    actionOrigin: string,
    onSuccess: (params: OnInjectionSuccessFnParams) => void,
    onFailure: (response: ErrorData) => void,
  ) => {
    const businessDescription = isFunnelDataApplicable
      ? await new Promise<string>((resolve) => {
          getDescriptionFromFunnel(resolve);
        })
      : '';
    const isAutosubmitFlow = true;

    return onSubmit(
      {
        businessName,
        businessType,
        businessDescription,
      },
      isAutosubmitFlow,
      actionOrigin,
      onSuccess,
      onFailure,
    );
  };

  return {
    ...ownProps,
    ...restDispatchProps,
    onSubmit,
    autoSubmitForm,
  };
};

export { mapStateToProps, mapDispatchToProps, mergeProps };
